import '../stylesheets/main.scss';

import * as Sentry from '@sentry/browser';
import htmx from 'htmx.org';
import Alpine from 'alpinejs';
import intersect from '@alpinejs/intersect';

// Alpine.js stores
// See https://alpinejs.dev/globals/alpine-store
import ReducedMotionStore from './stores/reduced-motion';

// Alpine.js directives
// See https://alpinejs.dev/advanced/extending#custom-directives
import MediaQueryDirective from './directives/media-query';
import ResizeObserverDirective from './directives/resizeobserver';

// Alpine.js components
// See https://alpinejs.dev/globals/alpine-data#registering-from-a-bundle
import TagButtonsComponent from '../components/tag-buttons/javascripts/index';
import ScrollContainerComponent from '../components/scroll-container/javascripts';
import VideoComponent from '../components/video/javascripts';
import DialogComponent from '../components/dialog/javascripts';
import TabsComponent from '../components/tabs/javascripts';
import AccordionTeaserComponent from '../components/accordion-teaser/javascripts';
import NavigationComponent from '../components/navigation/javascripts';
import TagsComponent from '../components/tags/javascripts';
import BalanceMaskComponent from '../components/balance-mask/javascripts';
import HistoryBlockComponent from '../components/history-block/javascripts/index';

import '../components/dithered-image/javascripts';

Sentry.init({
  enabled: window.SENTRY_ENABLED,

  dsn: 'https://6a4edaf2aa95ac06b2a14526f9c370b9@report.fork.de/2',
  integrations: [Sentry.browserTracingIntegration()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: window.SENTRY_TRACES_SAMPLE_RATE,

  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    /global code/,

    'ChunkLoadError',

    // Firefox message when an extension tries to modify a no-longer-existing DOM node
    // See https://blog.mozilla.org/addons/2012/09/12/what-does-cant-access-dead-object-mean/
    /can't access dead object/
  ],

  denyUrls: [
    // https://googleads.g.doubleclick.net/
    /doubleclick\.net/i,
    // https://www.googletagmanager.com/gtag/js
    /googletagmanager\.com/i,
    // https://cdn-ukwest.onetrust.com/
    /onetrust\.com/i,
    // https://js-eu1.hs-scripts.com/
    /hs-scripts\.com/i,
    // https://js-eu1.hsforms.net/
    /hsforms\.net/i,

    // Browser extensions
    /extensions\//i,
    /^chrome(-extension)?:\/\//i,
    /^safari(-web)?-extension:\/\//i,
    /^moz-extension:\/\//i,
    /webkit-masked-url/i // @webkit-masked-url(://hidden/)
  ]
});

// Alpine.js initialization
// See https://alpinejs.dev/essentials/installation#as-a-module
window.Alpine = Alpine;

// htmx initialization
// See https://htmx.org/docs/#webpack
window.htmx = htmx;

Alpine.plugin(intersect);

Alpine.store('reducedMotion', ReducedMotionStore);

Alpine.directive('mq', MediaQueryDirective);
Alpine.directive('resizeobserver', ResizeObserverDirective);

Alpine.data('ScrollContainer', ScrollContainerComponent);
Alpine.data('Video', VideoComponent);
Alpine.data('Dialog', DialogComponent);
Alpine.data('Tabs', TabsComponent);
Alpine.data('AccordionTeaser', AccordionTeaserComponent);
Alpine.data('Navigation', NavigationComponent);
Alpine.data('Tags', TagsComponent);
Alpine.data('BalanceMask', BalanceMaskComponent);
Alpine.data('TagButtons', TagButtonsComponent);
Alpine.data('HistoryBlock', HistoryBlockComponent);

Alpine.start();

// All links with target="_blank" attribute should have an attribute rel="noopener". This has performance and security reasons https://developers.google.com/web/tools/lighthouse/audits/noopener
document.querySelectorAll("[target='_blank']").forEach((link) => {
  link.setAttribute('rel', 'noopener');
});

const REDUCE_MOTION_KEY = 'prefers-reduced-motion';

const initReduceMotion = () => {
  // Sync the prefers-reduced-motion browser setting into the Alpine store
  const mq = window.matchMedia('(prefers-reduced-motion)');
  mq.addEventListener('change', (e) => {
    (Alpine.store('reducedMotion') as typeof ReducedMotionStore).reduceMotion =
      e.matches;
  });

  // Sync the localstorage reduce-motion setting into the Alpine store
  // localStorage only overrides the browser setting if it is set to
  // reduce motion and the browser setting is not.
  const storedValue = window.localStorage.getItem(REDUCE_MOTION_KEY);
  const initialValue = storedValue === null ? mq.matches : storedValue === '1';
  (Alpine.store('reducedMotion') as typeof ReducedMotionStore).reduceMotion =
    initialValue;

  // Continuously sync the current user setting into localStorage
  Alpine.effect(() => {
    const reduceMotion = (
      Alpine.store('reducedMotion') as typeof ReducedMotionStore
    ).reduceMotion;

    if (reduceMotion) {
      window.localStorage.setItem(REDUCE_MOTION_KEY, '1');
    } else {
      window.localStorage.removeItem(REDUCE_MOTION_KEY);
    }

    document.body.classList.toggle('reduce-motion', reduceMotion);
  });
};

initReduceMotion();
